import {Button, Card, CardContent} from '@nlabs/gothamjs';

import chicagoHero from '../../assets/chicago.png';
import educationImage from '../../assets/education.png';
import galleryImage from '../../assets/gallery.png';
import homeLogo from '../../assets/logo-transparent.png';
import meetupImage from '../../assets/meetup.png';
import {Page} from '../../components/Page';
import {reveal} from '../../lib/styles';
import {Route} from '../../types/routes';

const outlineButton = 'inline-flex min-h-12 cursor-pointer items-center justify-center rounded-none border border-[var(--nx-ink)] bg-transparent px-7 text-sm font-extrabold text-[var(--nx-ink)] transition-colors hover:bg-[var(--nx-ink)] hover:text-white';
const darkButton = 'inline-flex min-h-12 cursor-pointer items-center justify-center rounded-none border border-[var(--nx-ink)] bg-[var(--nx-ink)] px-7 text-sm font-extrabold text-white transition-colors hover:bg-transparent hover:text-[var(--nx-ink)]';

const programs = [
  {
    body: 'Exhibitions, installations, artist residencies, community showcases, and cultural programming rooted in Chicago creative energy.',
    label: '01',
    title: 'Arts & Culture'
  },
  {
    body: 'Workshops, youth programs, creative coding, digital storytelling, AI literacy, and arts-tech learning pathways.',
    label: '02',
    title: 'Education'
  },
  {
    body: 'Creative technology labs exploring immersive media, projection, XR, AI-assisted art, and platforms like TorchOne.',
    label: '03',
    title: 'Technology'
  }
];

const impactStats = [
  ['Artists', 'Residencies, showcases, and collaborative programming'],
  ['Youth', 'Creative technology access and hands-on learning'],
  ['Events', 'Talks, screenings, workshops, and public labs'],
  ['Platforms', 'Digital systems that extend programs beyond the room']
];

const experiences = [
  {
    body: 'Gallery nights, artist showcases, and public installations that make community culture visible.',
    image: galleryImage,
    label: 'Exhibitions',
    title: 'Neighborhood-scale creative moments'
  },
  {
    body: 'Creative coding, AI literacy, digital storytelling, immersive media, and youth workshops.',
    image: educationImage,
    label: 'Labs',
    title: 'Education with tools people can touch'
  },
  {
    body: 'Panels, performances, screenings, meetups, and talks around the future of creativity.',
    image: meetupImage,
    label: 'Events',
    title: 'Culture as a shared room'
  }
];

const stories = [
  'Artist spotlights from the UFAT community',
  'Event recaps and field notes from Chicago programming',
  'Essays on AI, education, culture, and human creativity'
];

const scrollToSection = (id: string) => {
  window.history.pushState({}, '', `#${id}`);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
  document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

const SectionShell = ({
  children,
  className = '',
  id,
  showGrid = true,
  title
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  showGrid?: boolean;
  title: string;
}) => (
  <section className={`${reveal} border-t border-[rgba(16,22,36,.16)] bg-white px-5 sm:px-8 lg:px-[70px] ${className}`} id={id}>
    <div className={`mx-auto grid max-w-[1200px] border-x border-[rgba(16,22,36,.14)] ${showGrid ? 'bg-[linear-gradient(90deg,rgba(16,22,36,.1)_1px,transparent_1px)] bg-[length:33.333%_100%]' : 'bg-white'}`}>
      <div className="grid gap-9 px-6 py-14 sm:px-8 lg:px-10 lg:py-20">
        <h2 className="m-0 max-w-[820px] text-4xl font-extrabold leading-none text-[var(--nx-ink)] sm:text-5xl lg:text-6xl">{title}</h2>
        {children}
      </div>
    </div>
  </section>
);

export const Home = ({route}: {route: Route}) => (
  <Page route={route}>
    <section className={`${reveal} min-h-screen bg-[#f6f6f3] px-5 pt-[72px] text-[var(--nx-ink)] sm:px-8 lg:px-[70px]`}>
      <div className="mx-auto grid min-h-[calc(100vh-72px)] max-w-[1200px] border-x border-[rgba(16,22,36,.16)] bg-[linear-gradient(90deg,rgba(16,22,36,.12)_1px,transparent_1px)] bg-[length:33.333%_100%] lg:grid-cols-[1.08fr_.92fr]">
        <div className="grid content-center px-6 py-16 sm:px-8 lg:px-0 lg:py-24">
          <div className="max-w-[650px] lg:px-10">
            <img className="mx-auto mb-7 h-[300px] w-[300px] object-contain" src={homeLogo} alt="UFAT logo" />
            <p className="mb-6 mt-0 text-xs font-extrabold uppercase tracking-[.18em] text-[rgba(16,22,36,.72)]">Art. Technology. Community.</p>
            <h1 className="mb-6 mt-0 text-4xl font-extrabold leading-none text-[var(--nx-ink)] sm:text-5xl lg:text-6xl">
              Building the future of creative culture in Chicago and beyond.
            </h1>
            <p className="m-0 max-w-[600px] text-base leading-7 text-[rgba(16,22,36,.74)] sm:text-lg sm:leading-8">
              UFAT brings artists, technologists, educators, and communities together through exhibitions, workshops, public programs, digital platforms, and future-facing creative education.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button className={darkButton} onClick={() => scrollToSection('programs')}>Explore Programs</Button>
              <Button className={outlineButton} onClick={() => scrollToSection('events')}>Upcoming Events</Button>
            </div>
          </div>
        </div>
        <div className="relative min-h-[420px] border-t border-[rgba(16,22,36,.16)] lg:border-l lg:border-t-0">
          <img className="absolute inset-0 h-full w-full object-cover object-center" src={chicagoHero} alt="Colorful painted Chicago skyline with abstract geometric shapes" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(246,246,243,.16),rgba(246,246,243,0)_45%),linear-gradient(180deg,rgba(16,22,36,0)_55%,rgba(16,22,36,.34))]" />
          <div className="absolute bottom-0 left-0 right-0 border-t border-white/35 bg-white/74 p-5 backdrop-blur-sm sm:p-7">
            <p className="m-0 max-w-[520px] text-base font-bold leading-7 text-[var(--nx-ink)]">
              A nonprofit arts-tech platform with Chicago grit, cultural warmth, and a studio-minded approach to what comes next.
            </p>
          </div>
        </div>
      </div>
    </section>

    <SectionShell title="Not a nonprofit brochure. A cultural platform.">
      <div className="grid gap-8 lg:grid-cols-[.82fr_1.18fr]">
        <p className="m-0 bg-white text-2xl font-extrabold leading-tight text-[var(--nx-ink)] sm:text-3xl">
          UFAT should feel like a movement people want to belong to.
        </p>
        <div className="grid gap-5 bg-white text-base leading-8 text-[rgba(52,64,84,.88)] sm:text-lg">
          <p className="m-0">
            Most nonprofit arts organizations either lean traditional, grassroots, STEM-generic, or grant-website static. UFAT can merge art, technology, culture, education, creative community, real-world events, and digital platforms into one cohesive identity.
          </p>
          <p className="m-0">
            The mission is to democratize access to creative tools, preserve human creativity in a technological age, support artists and makers, and give underserved communities a real place inside the future of culture.
          </p>
        </div>
      </div>
    </SectionShell>

    <SectionShell id="programs" title="Programs & initiatives">
      <div className="grid gap-0 border border-[rgba(16,22,36,.16)] lg:grid-cols-3">
        {programs.map((program) => (
          <Card className="rounded-none border-0 border-b border-[rgba(16,22,36,.16)] bg-white shadow-none lg:border-b-0 lg:border-r last:border-b-0 last:lg:border-r-0" key={program.title}>
            <CardContent className="grid min-h-[280px] content-start gap-8 p-7 sm:p-8">
              <div className="flex items-center justify-between gap-4">
                <p className="m-0 text-xs font-extrabold uppercase tracking-[.18em] text-[var(--nx-green)]">{program.label}</p>
                <span className="h-px flex-1 bg-[rgba(16,22,36,.16)]" />
              </div>
              <div>
                <h3 className="mb-3 mt-0 text-3xl font-extrabold leading-none text-[var(--nx-ink)]">{program.title}</h3>
                <p className="m-0 leading-7 text-[rgba(52,64,84,.84)]">{program.body}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </SectionShell>

    <SectionShell id="impact" title="Impact in motion">
      <div className="grid gap-9 lg:grid-cols-[.9fr_1.1fr]">
        <div className="grid content-start gap-5">
          <p className="m-0 bg-white text-2xl font-extrabold leading-tight text-[var(--nx-ink)] sm:text-3xl">
            Launching accessible creative technology programming with measurable community value.
          </p>
          <p className="m-0 bg-white leading-8 text-[rgba(52,64,84,.82)]">
            Impact can be tracked through people served, artists supported, youth engagement, exhibitions produced, workshops hosted, neighborhoods reached, and platforms launched.
          </p>
        </div>
        <div className="grid border border-[rgba(16,22,36,.16)] bg-white sm:grid-cols-2">
          {impactStats.map(([label, body]) => (
            <div className="min-h-[170px] border-b border-[rgba(16,22,36,.16)] p-6 even:sm:border-l sm:[&:nth-last-child(-n+2)]:border-b-0" key={label}>
              <h3 className="m-0 text-3xl font-extrabold leading-none text-[var(--nx-ink)]">{label}</h3>
              <p className="mb-0 mt-5 leading-7 text-[rgba(52,64,84,.82)]">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>

    <SectionShell id="events" title="Experiences shaping Chicago's creative future">
      <div className="grid gap-6 lg:grid-cols-3">
        {experiences.map((experience) => (
          <article className={`${reveal} grid overflow-hidden border border-[rgba(16,22,36,.16)] bg-white`} key={experience.title}>
            <div className="aspect-[4/3] overflow-hidden border-b border-[rgba(16,22,36,.16)]">
              <img className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" src={experience.image} alt="" />
            </div>
            <div className="grid min-h-[260px] content-start gap-6 p-6">
              <div>
                <p className="m-0 text-xs font-extrabold uppercase tracking-[.18em] text-[var(--nx-gold)]">{experience.label}</p>
                <h3 className="mb-0 mt-4 text-2xl font-extrabold leading-tight text-[var(--nx-ink)]">{experience.title}</h3>
              </div>
              <p className="m-0 leading-7 text-[rgba(52,64,84,.84)]">{experience.body}</p>
            </div>
          </article>
        ))}
      </div>
    </SectionShell>

    <SectionShell id="about" showGrid={false} title="Chicago roots. Future-facing culture.">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_.9fr]">
        <div className="grid gap-5 bg-white text-base leading-8 text-[rgba(52,64,84,.88)] sm:text-lg">
          <p className="m-0 text-2xl font-extrabold leading-tight text-[var(--nx-ink)] sm:text-3xl">
            UFAT can honor the Melodye Rosales legacy, the Pilsen studio space, and the energy of local creators while building cultural infrastructure for the next generation.
          </p>
          <p className="m-0">
            Nonprofit status, transparency, governance, EIN details, and annual reporting belong here too, presented with clarity without taking over the emotional first impression.
          </p>
        </div>
        <div className="grid border border-[rgba(16,22,36,.16)] bg-white">
          {['Local Artists', 'Schools', 'Universities', 'Foundations', 'Creative Studios'].map((partner) => (
            <div className="border-b border-[rgba(16,22,36,.16)] px-6 py-5 text-lg font-extrabold last:border-b-0" key={partner}>{partner}</div>
          ))}
        </div>
      </div>
    </SectionShell>

    <SectionShell id="stories" title="Journal & stories">
      <div className="grid gap-8 lg:grid-cols-[.78fr_1.22fr]">
        <p className="m-0 bg-white text-2xl font-extrabold leading-tight text-[var(--nx-ink)] sm:text-3xl">
          This is where UFAT becomes intellectually cool, searchable, grant-visible, and culturally useful.
        </p>
        <div className="grid border border-[rgba(16,22,36,.16)] bg-white">
          {stories.map((story) => (
            <div className="border-b border-[rgba(16,22,36,.16)] p-6 text-xl font-extrabold leading-tight last:border-b-0" key={story}>{story}</div>
          ))}
        </div>
      </div>
    </SectionShell>
  </Page>
);
